import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        Kupić mieszkanie na kredyt czy wynajmować? To jedno z najważniejszych pytań finansowych,
        z którymi mierzy się większość Polaków. Odpowiedź zależy od wielu czynników — od Twojej
        sytuacji życiowej, stabilności zawodowej, a przede wszystkim od konkretnych liczb.
        Sprawdźmy, co warto wziąć pod uwagę.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Koszty kupna — nie tylko rata</h2>
      <p>
        Kiedy kupujesz mieszkanie na kredyt, płacisz znacznie więcej niż samą ratę. Do kosztów
        kupna należy doliczyć:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong><BlogLink slug="wklad-wlasny-kredyt-hipoteczny">Wkład własny</BlogLink></strong> — minimum 10-20% wartości nieruchomości, czyli np. 100 000 zł przy mieszkaniu za 500 000 zł</li>
        <li><strong>Odsetki od kredytu</strong> — przy oprocentowaniu ~7,85% przez 25 lat zapłacisz bankowi ponad 500 000 zł samych odsetek</li>
        <li><strong>Prowizja banku</strong> — jednorazowo 0-2% kwoty kredytu</li>
        <li><strong>Ubezpieczenia</strong> — nieruchomości, na życie, niskiego wkładu własnego</li>
        <li><strong>Notariusz i PCC</strong> — podatek od czynności cywilnoprawnych (2% na rynku wtórnym) plus taksa notarialna</li>
      </ul>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20}>
          Oblicz pełny koszt kredytu dla swojego mieszkania
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Koszty wynajmu — prostsze, ale bez zwrotu</h2>
      <p>
        Wynajem jest prostszy finansowo — płacisz czynsz i kaucję. Nie musisz mieć oszczędności
        na wkład własny, nie martwisz się o naprawy i utrzymanie. Ale jest jeden kluczowy minus:
        po 25 latach wynajmowania nie masz nic. Wszystkie wpłacone pieniądze są wydatkiem, nie
        inwestycją.
      </p>
      <p className="mt-3">
        Przy czynszu 3 000 zł miesięcznie przez 25 lat wydasz łącznie <strong>900 000 zł</strong> —
        i nie zostaniesz z żadnym majątkiem. Przy kredycie wydasz więcej w sumie, ale po spłacie
        masz mieszkanie warte kilkaset tysięcy złotych.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kiedy kredyt się opłaca?</h2>
      <p>
        Kluczowy moment to <strong>punkt zwrotny</strong> — rok, w którym sumaryczne wydatki na
        wynajem przekraczają sumaryczne wydatki na kredyt. Dlaczego wynajem na początku jest tańszy?
        Bo nie płacisz wkładu własnego (np. 100 000 zł z góry).
      </p>
      <p className="mt-3">
        Ale jeśli Twoja rata kredytu jest niższa od czynszu najmu, co miesiąc oszczędzasz różnicę.
        Z czasem ta różnica "nadrabia" wkład własny. Na przykład:
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Przykład — mieszkanie 500 000 zł:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Rata kredytu: ~3 048 zł/mies.</li>
          <li>Czynsz najmu: 5 000 zł/mies.</li>
          <li>Różnica: 1 952 zł/mies. na korzyść kredytu</li>
          <li>Wkład własny: 100 000 zł</li>
          <li><strong>Punkt zwrotny: ~rok 5</strong> — od tego momentu wynajem kosztuje więcej</li>
        </ul>
      </div>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} monthlyRent={5000} section="rent-vs-buy">
          Sprawdź punkt zwrotny dla swoich parametrów
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kiedy lepiej wynajmować?</h2>
      <p>
        Wynajem nie zawsze jest gorszą opcją. Warto go rozważyć, gdy:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Planujesz się przeprowadzić</strong> — jeśli nie zamierzasz mieszkać w jednym miejscu dłużej niż 5-7 lat, koszty transakcyjne kupna mogą się nie zwrócić</li>
        <li><strong>Nie masz wkładu własnego</strong> — zbieranie na 20% wkładu może trwać lata (sprawdź <BlogLink slug="ile-zarabiac-na-kredyt-hipoteczny">ile musisz zarabiać</BlogLink>, żeby w ogóle dostać kredyt), a w tym czasie ceny mogą rosnąć</li>
        <li><strong>Czynsz jest znacznie niższy od raty</strong> — jeśli różnica jest duża, a nadwyżkę inwestujesz, wynajem może wygrać finansowo</li>
        <li><strong>Rynek jest niepewny</strong> — w okresach niestabilności trudno przewidzieć wartość nieruchomości</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kiedy lepiej kupić?</h2>
      <p>
        Kupno ma sens, gdy:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Planujesz zostać na długo</strong> — im dłuższy horyzont, tym bardziej opłaca się kupno</li>
        <li><strong>Masz wkład własny</strong> — 20% to ideał, ale 10% z ubezpieczeniem też działa</li>
        <li><strong>Rata jest zbliżona do czynszu</strong> — jeśli płacisz podobnie, ale budujesz majątek — wybór jest oczywisty</li>
        <li><strong>Chcesz stabilności</strong> — właściciel nie podniesie Ci czynszu i nie wypowie umowy</li>
        <li><strong>Traktujesz to jako inwestycję</strong> — nieruchomości historycznie zyskują na wartości</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czego nie uwzględnia prosty kalkulator?</h2>
      <p>
        Nasze porównanie to uproszczony model. W rzeczywistości warto pamiętać o dodatkowych czynnikach:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Wzrost cen nieruchomości</strong> — Twoje mieszkanie za 500 000 zł za 25 lat może być warte znacznie więcej</li>
        <li><strong>Wzrost czynszów</strong> — czynsz 3 000 zł dzisiaj za 10 lat może wynosić 5 000 zł</li>
        <li><strong>Inwestowanie nadwyżki</strong> — jeśli wynajmujesz taniej i inwestujesz różnicę, możesz zarobić więcej</li>
        <li><strong>Koszty utrzymania</strong> — jako właściciel płacisz za remonty, fundusz remontowy, ubezpieczenie</li>
        <li><strong>Zmiana stóp procentowych</strong> — przy zmiennym oprocentowaniu rata może wzrosnąć</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        Nie ma uniwersalnej odpowiedzi na pytanie "kupić czy wynajmować". Wszystko zależy od Twoich
        konkretnych liczb — wysokości raty, czynszu, wkładu własnego i horyzontu czasowego.
        Skorzystaj z naszego kalkulatora, aby porównać oba scenariusze dla swoich parametrów
        i znaleźć swój punkt zwrotny.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={400000} downPaymentPercent={20} monthlyRent={3000} section="rent-vs-buy">
          Porównaj kupno i wynajem w kalkulatorze RatyGo
        </CalculatorLink>
      </div>
    </>
  )
}
